import { Button } from '@shared/components/ui/button'
import { useConsent } from '@shared/contexts/ConsentContext'
import { usePWA } from '@shared/contexts/PWAContext'
import { useTourLifecycle } from '@shared/contexts/TourLifecycleContext'
import { X } from 'lucide-react'

/**
 * Cookie / analytics consent banner.
 *
 * PostHog is initialized with `opt_out_capturing_by_default: true` (see index.html),
 * so no analytics are captured until the user accepts here. Behaviour:
 *  - no decision / expired denial → show the banner; nothing is tracked yet
 *  - 'granted'                    → re-enable capturing on load, no banner (never expires)
 *  - 'denied' (within 3 weeks)    → stay opted out (the init default), no banner
 *
 * A 'denied' decision expires after 3 weeks (see consentStorage); once expired,
 * getConsent() returns null and the banner is shown again. 'granted' never expires.
 *
 * Essential cookies (auth/session) are unaffected — they are required for the
 * platform to function and are not gated by this banner.
 */
export function CookieConsentBanner() {
  const { decision, accept, dismiss } = useConsent()
  const { isPromptVisible } = usePWA()
  const { isTourActive } = useTourLifecycle()

  if (decision !== null || isPromptVisible || isTourActive) return null

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de cookies"
      className="fixed bottom-0 left-0 right-0 z-[60] border-t border-border bg-card p-4 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]"
    >
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-3 top-3"
        onClick={dismiss}
        aria-label="Cerrar aviso de cookies"
      >
        <X className="h-4 w-4" />
      </Button>
      <div className="mx-auto flex max-w-3xl flex-col gap-3 pr-10 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Usamos cookies propias y de terceros para mantener tu sesión y analizar el uso de la
          plataforma y así mejorar tu experiencia. Las cookies de analítica solo se activan si las
          aceptás.
        </p>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" onClick={accept}>
            Aceptar
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CookieConsentBanner
